<template>
  <div class="container">
    <div class="row" v-if="hasData && showIndexDetail">
      <div class="col">
        <h5 class="card-header bg-primary text-white">Risk Index Finding Summary</h5>
        <div v-for="category in this.categories" :key="category.code" :class="getBreakStatus(category)" class="card mb-3">
          <div class="card-body p-0">
            <div class="container-fluid">
              <div class="row">
                <div class="col">
                  <div class="row mt-2">
                    <div class="mt-2 ml-3">
                      <CategoryIcon :categoryCode="category.code" />
                    </div>
                    <div class="col">
                      <h4>{{ category.displayName }}</h4>
                      <div>
                        <span class="p-1" :class="getBgClass(category)">Score: {{ getCurrentScoreLabel(category) }}</span>
                        <span v-if="showOriginalScore(category)" class="p-1 ml-2" :class="getOriginalBgClass(category)">
                          Original Score: {{ getOriginalScoreLabel(category) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="row mt-2" v-if="categoryHasHeadline(category)">
                    <div class="ml-3">
                      <h5 class="">Summary:</h5>
                    </div>
                  </div>
                  <div class="row" v-if="categoryHasHeadline(category)">
                    <div class="col mx-1">
                      <p class="mb-0 text-justify">
                        {{ getHeadline(category) }}
                      </p>
                    </div>
                  </div>
                  <div class="card my-2" v-for="comment in comments('category', category.code)" :key="comment.commentId">
                    <div class="card-body py-1">
                      <p class="font-italic">{{ comment.text }}</p>
                      <p class="mb-0 text-muted">{{ comment.userFullName }} on {{ formatter.date.long(comment.timestamp) }}</p>
                    </div>
                  </div>
                  <div class="row d-print-inline">
                    <div class="col px-0">
                      <table class="table mt-1" v-if="hasQuestionsToViewInCategory(category)">
                        <thead>
                          <tr>
                            <th></th>
                            <th class="text-left">Question</th>
                            <th class="text-center w-25">Response</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="response in questionsToView(category)" :key="response.responseId">
                            <td class="align-top"><fa :icon="getResponseIcon(response)" size="lg" :class="getResponseIconClass(response)" /></td>
                            <td class="text-left align-top">
                              <span>{{ response.text }}</span>
                              <div class="alert alert-warning mt-2 mb-0 p-1" v-if="response.evasionCommentary">{{ response.evasionCommentary }}</div>
                              <div class="alert alert-secondary mt-2 mb-0 p-1" v-if="response.editorCode">{{ getResponseEditDescription(response) }}</div>
                              <div v-for="comment in comments('response', response.responseId)" :key="comment.commentId">
                                <div class="card-body py-0 pt-1">
                                  <p class="font-italic">{{ comment.text }}</p>
                                  <p class="mb-0 text-muted">{{ comment.userFullName }} on {{ formatter.date.long(comment.timestamp) }}</p>
                                </div>
                              </div>
                            </td>
                            <td class="text-center align-top text-capitalize">{{ getResponseDisplayName(response) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CategoryIcon from "@/views/reports/report/edit-report/CategoryIcon.vue";
import formatter from "@/utils/formatter";

export default {
  components: {
    CategoryIcon,
  },
  props: {
    organizationId: String,
    reportId: String,
  },
  data() {
    return {
      report: null,
    };
  },
  computed: {
    ...mapGetters("static", ["reportResponseEdits"]),
    formatter: () => formatter,
    hasData() {
      if (this.report && this.report.summary && this.report.summary.scoreCode === "inconclusive") return false;
      if (this.statusCode === "completed") return true;
      return false;
    },
    statusCode() {
      if (this.report && this.report.statusCode) return this.report.statusCode;
      return "unknown";
    },
    showIndexDetail() {
      return this.report != null && this.report.statusCode === "completed";
    },
    categories() {
      var categories = this.report ? (this.report.riskIndex ? this.report.riskIndex.categories : []) : [];
      var sortedCategories = categories.slice();
      sortedCategories.sort((x, y) => {
        if (x.displayName < y.displayName) return -1;
        if (x.displayName > y.displayName) return 1;
        return 0;
      });

      sortedCategories.forEach(category => {
        category.questions.sort((x, y) => {
          var xSort =
            x.responseDisplayName !== x.preferredResponseDisplayName || (x.controlCode === "1" && !x.evasionCommentary) ? 1 : x.evasionCommentary ? 2 : 3;
          var ySort =
            y.responseDisplayName !== y.preferredResponseDisplayName || (y.controlCode === "1" && !y.evasionCommentary) ? 1 : y.evasionCommentary ? 2 : 3;

          if (xSort < ySort) return -1;
          if (xSort > ySort) return 1;
          return 0;
        });
      });

      return sortedCategories;
    },
  },
  created() {
    this.$axios
      .get(`/v1/organizations/${this.organizationId}/reports/${this.reportId}`)
      .then(res => (this.report = res.data))
      .catch(err => (this.error = err))
      .finally(() => this.print());
  },
  methods: {
    comments(subjectType, subjectId) {
      if (!this.report || !this.report.comments) return [];
      return this.report.comments.filter(c => c.subjectType === subjectType && c.subjectId == subjectId);
    },
    getBreakStatus(category) {
      if (this.shouldPageBreak(category)) {
        return "break-before";
      }
      return "";
    },
    shouldPageBreak(category) {
      const heightOfPage = 50;
      //find the previous categories
      var previousCategories = [];
      for (var i = 0; i < this.categories.length; i++) {
        var currentCategory = this.categories[i];
        if (currentCategory.code === category.code) {
          break;
        }
        previousCategories.push(currentCategory);
      }

      var totalHeight = 0;
      totalHeight += 3; // for first page header
      console.log("CALCULATING PREVIOUS CATEGORY HEIGHTS");
      for (var j = 0; j < previousCategories.length; j++) {
        var previousCategory = previousCategories[j];
        var height = this.getVerticalSpaceForCategory(previousCategory);
        console.log(`category: ${previousCategory.code} height: ${height}`);
        if (totalHeight + height >= heightOfPage) {
          console.log("going to next page, resetting height");
          totalHeight = height;
        } else {
          totalHeight += height;
        }
      }
      console.log(`total height on active page: ${totalHeight}`);

      var remainingSpace = heightOfPage - totalHeight;
      console.log(`remaining space on current page: ${remainingSpace}`);

      // calculation space for current cateogry
      var currentCategoryHeight = this.getVerticalSpaceForCategory(category);
      console.log(`current category: ${category.code} height: ${currentCategoryHeight}`);

      return currentCategoryHeight > remainingSpace;
    },
    getVerticalSpaceForCategory(category) {
      var totalHeight = 0;
      totalHeight += 5; // for title

      var headline = this.getHeadline(category);
      if (headline && headline.length > 0) {
        totalHeight += 2; // for Summary title
        totalHeight += Math.ceil(headline.length / 100);
      }
      var categoryComments = this.comments("category", category.code);
      totalHeight += categoryComments.length * 4;

      totalHeight += 1.5; // question table header
      this.questionsToView(category).forEach(question => {
        totalHeight += question.text.length <= 85 ? 1.3 : 2;
        if (question.evasionCommentary) {
          totalHeight += question.evasionCommentary.length <= 100 ? 1.3 : 2;
        }
        if (question.editorCode) {
          totalHeight += this.getResponseEditDescription(question).length <= 100 ? 1.3 : 2;
        }

        var responseComments = this.comments("response", question.responseId);
        totalHeight += responseComments.length * 4;
      });

      totalHeight = Math.ceil(totalHeight);
      return totalHeight;
    },
    categoryHasHeadline(category) {
      return (category != null && category.headline != null && category.headline.length > 0) || !this.hasQuestionsToViewInCategory(category);
    },
    hasQuestionsToViewInCategory(category) {
      return this.questionsToView(category).length;
    },
    questionsToView(category) {
      return category.questions.filter(q => q.groupCode != "5");
    },
    getHeadline(category) {
      if (category.headline) return category.headline;

      if (!this.hasQuestionsToViewInCategory(category)) {
        return `The system analytics and the examinee's responses did not identify counterproductive workplace behaviors related to ${category.displayName}.`;
      }

      return "";
    },
    getIcon(category) {
      if (!category.currentScore) return "circle";
      if (category.currentScore >= 70) return "check";
      if (category.currentScore >= 50) return "question";
      return "times";
    },
    getBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];
      if (category.currentScore >= 70) return ["bg-success", "text-white"];
      if (category.currentScore >= 50) return ["bg-warning", "text-white"];
      return ["bg-danger", "text-white"];
    },
    getOriginalBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];
      if (category.originalScore >= 70) return ["bg-success", "text-white"];
      if (category.originalScore >= 50) return ["bg-warning", "text-white"];
      return ["bg-danger", "text-white"];
    },
    getResponseIcon(response) {
      if (response.controlCode === "1") return "exclamation";
      if (response.evasionCommentary) return "exclamation";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "exclamation";
      return "check";
    },
    getResponseIconClass(response) {
      if (response.controlCode === "1" && !response.evasionCommentary) return "text-danger";
      if (response.evasionCommentary) return "text-warning";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "text-danger";
      return "text-success";
    },
    getResponseEditDescription(response) {
      var record = this.reportResponseEdits.find(c => c.code === response.editorCode);
      return record ? record.displayName : "---";
    },
    getResponseDisplayName(response) {
      return response.responseDisplayName.toLowerCase() == "timeout" ? "The examinee avoided answering this question" : response.responseDisplayName;
    },
    isCategoryDisabled(category) {
      return category.statusCode != "enabled";
    },
    showOriginalScore(category) {
      return category.originalScore != category.currentScore;
    },
    print() {
      setTimeout(() => (window.printReady = true), 500);
    },
    getOriginalScoreLabel(selectedCategory) {
      if (!selectedCategory) return "";
      if (this.isCategoryDisabled(selectedCategory)) return "";
      if (selectedCategory.originalScore >= 70) return "Above Conditions";
      if (selectedCategory.originalScore >= 50) return "Needs Clarification";
      return "Below Conditions";
    },
    getCurrentScoreLabel(selectedCategory) {
      if (!selectedCategory) return "";
      if (this.isCategoryDisabled(selectedCategory)) return "";
      if (selectedCategory.currentScore >= 70) return "Above Conditions";
      if (selectedCategory.currentScore >= 50) return "Needs Clarification";
      return "Below Conditions";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .mb-0;

  tr {
    cursor: pointer;
  }

  th,
  td {
    @extend .text-center;
    @extend .align-middle;

    h4,
    h5 {
      @extend .mb-0;
    }
  }
}

.break-before {
  page-break-before: always;

  @extend .mt-5;
}
</style>
